import { Link } from "react-router-dom";
import {
  ArrowRight,
  Truck,
  ShieldCheck,
  Package,
  Calculator,
  Ruler,
  Users,
  BookOpen,
  Anchor,
  Scale,
  Layers,
  Sparkles,
  Boxes,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison sur mesure ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Livraison sur mesure
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Votre livraison de cartons à {city.name}
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Évitez la perte de temps liée aux trajets dans les grandes
            surfaces et la manutention fastidieuse de liasses
            d&apos;emballages encombrantes. Nous acheminons tout votre
            matériel directement à votre porte à {city.name}.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            {
              title: "Filière de transport interne",
              body: "Nous n'externalisons jamais nos livraisons. C'est notre propre société qui charge, transporte et dépose vos fournitures en Île-de-France, vous assurant un soin maximal et un respect strict des horaires convenus.",
              Icon: Truck,
            },
            {
              title: "Matériel sec et rigide",
              body: "Nos véhicules fermés préservent vos cartons des intempéries et de l'humidité. Vous réceptionnez des contenants à la géométrie impeccable, prêts à être montés et scellés.",
              Icon: ShieldCheck,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[16px] text-[#0f1d2b]">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Calculateur formules : estimer son volume ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FFE9D6] border border-[#FF7A00]/30 text-[11px] uppercase tracking-[0.18em] font-bold text-[#FF7A00] mb-4">
            <Calculator className="h-3.5 w-3.5" strokeWidth={2.6} />
            Calculateur logistique
          </div>
          <h2 className="font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Comment bien estimer votre volume de cartons&nbsp;?
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            L&apos;une des clés d&apos;un déménagement réussi est
            d&apos;éviter la panne de matériel au milieu de vos
            préparatifs. Pour évaluer précisément le nombre de cartons
            nécessaires pour votre logement à {city.name}, nos experts
            vous conseillent d&apos;appliquer ces règles logistiques.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            {
              tag: "Ratio surface",
              formula: "1 → 1,5",
              unit: "carton / m²",
              title: "Le ratio par mètre carré",
              body: "En moyenne, on estime qu'il faut prévoir environ 1 à 1,5 carton par mètre carré de surface habitable. Pour un appartement de 40 m², tablez sur une base de 40 à 60 cartons.",
              Icon: Ruler,
            },
            {
              tag: "Profil ménage",
              formula: "20 → 30",
              unit: "cartons / adulte",
              title: "Le profil des occupants",
              body: "Un logement minimaliste ne demande pas la même logistique qu'une maison familiale. Multipliez le nombre d'adultes par 20 à 30 cartons, et comptez une quinzaine de boîtes par enfant pour les jouets et les vêtements.",
              Icon: Users,
            },
            {
              tag: "Volumes denses",
              formula: "3 → 5",
              unit: "cartons / étagère",
              title: "La règle des volumes denses",
              body: "Ne sous-estimez pas les pièces techniques. Une seule étagère de bibliothèque ou un buffet de cuisine requiert souvent entre 3 et 5 cartons de petit format (spécial livres ou vaisselle) pour être intégralement vidés.",
              Icon: BookOpen,
            },
          ].map(({ tag, formula, unit, title, body, Icon }) => (
            <article
              key={title}
              className="bg-[#EEF3F7] rounded-2xl border border-[#cfdbe6] overflow-hidden flex flex-col"
            >
              {/* Formula band */}
              <div className="bg-gradient-to-br from-[#0f1d2b] to-[#1a2d3f] text-white p-6 md:p-7 relative overflow-hidden">
                <div
                  aria-hidden="true"
                  className="absolute -top-10 -right-10 h-32 w-32 rounded-full bg-[#237cb5]/30 blur-2xl"
                />
                <div className="relative flex items-start justify-between">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#FF7A00]">
                      {tag}
                    </div>
                    <div className="mt-3 font-display font-extrabold text-4xl md:text-5xl tracking-tight text-white leading-none">
                      {formula}
                    </div>
                    <div className="mt-2 text-[12px] uppercase tracking-[0.14em] font-bold text-white/70">
                      {unit}
                    </div>
                  </div>
                  <span className="flex items-center justify-center h-11 w-11 rounded-full bg-white/10 border border-white/15 text-white">
                    <Icon className="h-5 w-5" strokeWidth={2.4} />
                  </span>
                </div>
              </div>
              {/* Body */}
              <div className="p-5 md:p-6 flex-1">
                <h3 className="font-display font-extrabold text-[16.5px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-3">
          <Link
            to="/packs-cartons"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-meudon-cta-packs"
          >
            <Boxes className="h-4 w-4" strokeWidth={2.6} />
            Voir nos kits pré-calculés
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-meudon-cta-shop"
          >
            Voir tous les cartons
          </Link>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-white">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Secrets des pros : 3 conseils avec bordure latérale ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-[#cfdbe6] text-[11px] uppercase tracking-[0.18em] font-bold text-[#237cb5] mb-4">
            <Sparkles className="h-3.5 w-3.5" strokeWidth={2.6} />
            Les secrets des pros
          </div>
          <h2 className="font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Conseils pour bien préparer vos colis
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Une fois vos fournitures reçues, la méthode d&apos;emballage
            est cruciale pour éviter la casse lors des manipulations.
            Suivez ces principes fondamentaux pour sécuriser vos
            fermetures.
          </p>
        </div>

        <div className="mt-10 space-y-4">
          {[
            {
              title: "Verrouillez la structure",
              body: "Montez vos cartons en appliquant l'adhésif en forme de « H » sur le fond (une bande centrale et deux bandes sur les côtés). Cela consolide les rabats et empêche le fond de s'ouvrir sous le poids des objets.",
              Icon: Anchor,
              color: "#FF7A00",
            },
            {
              title: "Équilibrez les masses",
              body: "Ne chargez jamais un grand carton uniquement avec des éléments lourds. Répartissez les charges : mettez les objets denses au fond (livres, dossiers) et comblez l'espace supérieur avec des éléments légers (linge, coussins) pour maintenir un poids total inférieur à 20 kg.",
              Icon: Scale,
              color: "#237cb5",
            },
            {
              title: "Supprimez les espaces vides",
              body: "Un carton à moitié vide risque de s'écraser lorsque les déménageurs vont l'empiler dans le camion. Utilisez du film bulle, du papier de calage ou même des torchons pour combler les vides et bloquer le contenu afin que rien ne bouge pendant les virages.",
              Icon: Layers,
              color: "#FF7A00",
            },
          ].map(({ title, body, Icon, color }) => (
            <article
              key={title}
              className="relative bg-white rounded-2xl border border-[#cfdbe6] pl-6 md:pl-7 pr-6 py-6 md:py-7 overflow-hidden"
              style={{ borderLeft: `6px solid ${color}` }}
            >
              <div className="flex items-start gap-4">
                <span
                  className="flex items-center justify-center h-11 w-11 rounded-full text-white shrink-0"
                  style={{ backgroundColor: color }}
                >
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <div className="flex-1">
                  <h3 className="font-display font-extrabold text-[17px] text-[#0f1d2b] leading-tight">
                    {title}
                  </h3>
                  <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                    {body}
                  </p>
                </div>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-3">
          <Link
            to="/produit/rouleau-adhesif"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-meudon-cta-adhesif"
          >
            Voir le rouleau d&apos;adhésif
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/emballage-protection"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-meudon-cta-calage"
          >
            <Layers className="h-4 w-4" strokeWidth={2.6} />
            Voir les consommables de calage
          </Link>
        </div>
      </div>
    </section>
    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-meudon-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
